<template>
  <AuthDialog
    :model-value="show"
    :title="
      mode === 'login'
        ? 'Sign in'
        : mode === 'forgotPassword'
          ? 'Forgot Password'
          : 'Reset Password'
    "
    :snackbar="snackbar"
    @update:model-value="(v) => (show = v)"
  >
    <LoginForm
      v-if="mode === 'login'"
      :loading="loading"
      @submit="handleLogin"
      @forgot-password="mode = 'forgotPassword'"
      @google-login="handleGoogleLogin"
    />

    <ForgotPasswordForm
      v-else-if="mode === 'forgotPassword'"
      @notify="(n) => showSnack(n.text, n.color)"
      @sent="mode = 'login'"
      @back="mode = 'login'"
    />

    <ResetPasswordForm
      v-else
      :loading="loading"
      @submit="handleResetSubmit"
      @back="backToLogin"
    />
  </AuthDialog>
</template>

<script setup lang="ts">
  import { ref, watch } from 'vue';
  import { useRoute } from 'vue-router';
  import AuthDialog from '@/interfaces/components/auth/AuthDialog.vue';
  import LoginForm from '@/interfaces/components/auth/LoginForm.vue';
  import ResetPasswordForm from '@/interfaces/components/auth/ResetPasswordForm.vue';
  import ForgotPasswordForm from '@/interfaces/components/auth/ForgotPasswordForm.vue';
  import { useAuthController } from '@/interfaces/composables/auth/useAuthController';
  import { useSnack } from '@/interfaces/composables/auth/useSnack';
  import { useApiErrors } from '@/interfaces/composables/auth/useApiErrors';
  import {
  checkAuth,
  ensureSession,
  startGoogleAuth,
} from '@/interfaces/composables/auth/auth';
  import { usePasswordReset } from '@/interfaces/composables/auth/usePasswordReset';
  import {
    refreshChats,
    startNewChat,
    clearChats,
    loadSharedChat,
  } from '@/interfaces/composables/useChatManager';

  const route = useRoute();

  const { mode, show, token } = useAuthController();
  const { snackbar, show: showSnack, reset: resetSnack } = useSnack();
  const { toMessage } = useApiErrors();
  const { finishReset } = usePasswordReset();

  const loading = ref(false);

  async function handleLogin({
    username,
    password,
  }: {
    username: string;
    password: string;
  }) {
    loading.value = true;
    try {
      await checkAuth(username, password);
      await ensureSession();
      clearChats(); // Clear any shared/old chats before loading user's chats

      const sharedId = route.query.sharedId as string | undefined;
      if (sharedId) {
        // User came from a shared link - load the shared chat
        await loadSharedChat(sharedId);
      } else {
        // Regular login - load user's chats and start new chat
        await refreshChats();
        startNewChat();
      }

      show.value = false;
      showSnack('Login successful!', 'success');
    } catch (e: unknown) {
      const m = toMessage(e);
      showSnack(m.text, m.color);
    } finally {
      loading.value = false;
    }
  }

  async function handleResetSubmit({ newPassword }: { newPassword: string }) {
    if (!token.value) {
      showSnack('Missing token', 'error');
      return;
    }
    loading.value = true;
    try {
      await finishReset(token.value, newPassword);
      showSnack('Password updated! You can now sign in.', 'success');
      token.value = null;
      mode.value = 'login';
    } catch (e: unknown) {
      const m = toMessage(e);
      showSnack(m.text, m.color);
    } finally {
      loading.value = false;
    }
  }

  function backToLogin() {
    token.value = null;
    mode.value = 'login';
  }

  async function handleGoogleLogin() {
    loading.value = true;
    try {
      const url = await startGoogleAuth();
      window.location.href = url;
    } catch (e: unknown) {
      const m = toMessage(e);
      showSnack(m.text, m.color);
      loading.value = false;
    }
  }

  watch(
    () => show.value,
    (visible) => {
      resetSnack();
      if (visible) mode.value = 'login';
    }
  );
</script>

<style scoped lang="sass"></style>